import React, { useState, useMemo, useEffect } from 'react';
import { IconButton, Typography, Button, TextField, Box, Paper, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { COLORS } from '../../../shared/Theme';
import ToastStr from '../../../request/toastStr';
import { DialogCompanyProcess } from '../../../components/Dialog';
import { member as v_member, basic, Values, SNACKBAR_TIME, SPLASH_TIME } from '../../../request/values';

const SERVER = basic.server;

const useStyles = makeStyles((theme) => ({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		padding: 0,
		background: '#ffffff',
		minHeight: '100vh',
		position: 'relative',
		width: '100%',
		boxSizing: 'border-box',
		overflowX: 'hidden',
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		padding: '16px 20px',
		position: 'relative',
		marginTop: '2vh',
		maxWidth: '550px',
		width: '100%',
		boxSizing: 'border-box',
	},
	back: {
		position: 'absolute',
		left: 10,
		top: '50%',
		transform: 'translateY(-50%)',
		color: COLORS.primary || '#4a90e2',
	},
	image: {
		width: '100%',
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		'& img': {
			width: '50vw',
			height: 'auto',
			maxWidth: '250px',
		},
	},
	content: {
		flex: 1,
		padding: '40px 20px 80px',
		display: 'flex',
		flexDirection: 'column',
		maxWidth: '550px',
		width: '100%', // 추가: 부모의 100%를 차지하도록
		boxSizing: 'border-box',
	},
	section: {
		marginBottom: 36,
	},
	sectionTitle: {
		fontWeight: 700,
		marginBottom: 8,
		color: COLORS.WATER_BLUE,
		fontSize: 24,
	},
	roleToggleWrap: {
		display: 'flex',
		gap: 12,
	},

	roleBtn: {
		flex: 1,
		height: 50,
		borderRadius: 8,
		border: '1px solid #e5e5e5',
		background: '#fff',
		color: '#afafaf',
		boxShadow: 'none',
		textTransform: 'none',
		'&.active': {
			borderColor: COLORS.WATER_BLUE,
			color: '#fff',
			background: COLORS.WATER_BLUE,
			fontWeight: 700,
		},
	},
	companyHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
	},
	refreshBtn: {
		width: 24,
		height: 24,
		color: COLORS.WATER_BLUE,
	},
	companyListWrap: {
		marginTop: 8,
	},
	companyItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		background: '#ffffff',
		border: '1px solid #E5E5E5',
		color: '#afafaf',
		borderRadius: 8,
		padding: '14px 18px',
		fontSize: 14,
		marginTop: 12,
		cursor: 'pointer',
		transition: 'all .15s ease',
	},
	companyItemActive: {
		background: COLORS.WATER_BLUE,
		borderColor: COLORS.WATER_BLUE,
		color: '#fff',
		fontWeight: 700,
	},
	companyLeft: {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
	},
	radioOuter: {
		width: 20,
		height: 20,
		borderRadius: '50%',
		border: '2px solid #BFC7D0',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: '#fff',
	},
	radioInner: {
		width: 10,
		height: 10,
		borderRadius: '50%',
		background: 'transparent',
	},
	companyName: {
		fontWeight: 700,
		color: '#afafaf',
	},
	companyCeo: {
		color: '#afafaf',
		fontSize: 12,
	},

	radioOuterActive: {
		borderColor: '#fff',
		background: COLORS.WATER_BLUE,
	},
	radioInnerActive: {
		background: '#fff',
	},
	companyNameActive: { color: '#fff' },
	companyCeoActive: { color: '#EAF2FF' },
	listDivider: { marginTop: 8, marginBottom: 8 },
	cardGrid: {
		display: 'grid',
		gridTemplateColumns: '1fr 1fr',
		gap: 12,
	},
	typeCard: {
		borderRadius: 8,
		padding: '14px 24px',
		border: '1px solid #e5e5e5',
		background: '#fff',
		display: 'flex',
		alignItems: 'center',
		flexDirection: 'column',
		justifyContent: 'center',
		cursor: 'pointer',
		userSelect: 'none',
		transition: 'all .15s ease',
		'& img': { width: 64, height: 64, objectFit: 'contain' },
		'& .label': { marginTop: 12, color: '#afafaf', fontSize: 16 },
		'&.active': {
			borderColor: COLORS.WATER_BLUE,
			background: COLORS.WATER_BLUE,
			'& .label': { color: '#fff', fontWeight: 700 },
		},
	},
	nameField: {
		'& .MuiInputBase-input': {
			padding: '7px',
			fontSize: 16,
		},
	},
	footer: {
		position: 'fixed',
		left: 0,
		right: 0,
		bottom: 0,
		background: '#fff',
		borderTop: '1px solid #eee',
		display: 'flex',
		justifyContent: 'center',
		padding: 16,
	},
	submit: {
		width: '100%',
		maxWidth: 550,
		height: 48,
		borderRadius: 10,
		fontWeight: 700,
		textTransform: 'none',
		background: COLORS.WATER_BLUE,
		color: '#fff',
		'&:disabled': { background: '#BDBDBD', color: '#fff' },
		'&:hover': { background: COLORS.WATER_BLUE },
	},
}));

const CompanyProcess: React.FC<any> = (props) => {
	const classes = useStyles();
	const history = useHistory();
	const { enqueueSnackbar } = useSnackbar();
	const { t } = useTranslation('student');
	const { member, setMember } = props;

	const [role, setRole] = useState<'leader' | 'member' | null>(null);
	const [type, setType] = useState<'M' | 'D' | null>(null);
	const [companyName, setCompanyName] = useState('');

	const [companyList, setCompanyList] = useState<any[]>([]);
	// store selected company by stable primary key, not by list index
	const [selectedCompany, setSelectedCompany] = useState<number | null>(null); // selected company_num
	// Memoized helper to get selected company object by company_num
	const selectedCompanyObj = useMemo(() => {
		if (selectedCompany === null) return null;
		return companyList.find((c: any) => c.company_num === selectedCompany) ?? null;
	}, [companyList, selectedCompany]);
	const [loadingList, setLoadingList] = useState(false);
	const [open, setOpen] = useState(false);
	const [dialogData, setDialogData] = useState<{
		companyName: string;
		ceoName: string;
		companyType: string;
		submit: (grade?: string) => void;
	} | null>(null);

	const disabled = useMemo(() => {
		if (role === 'leader') return !type || companyName.trim().length === 0;
		if (role === 'member') return selectedCompany === null;
		return true;
	}, [role, type, companyName, selectedCompany]);

	const goBack = () => history.push({ pathname: '/NameInput' });

	const openDialog = () => {
		if (role === 'leader' && type !== null && companyName.trim().length > 0) {
			setDialogData({
				companyName: companyName,
				ceoName: member.name,
				companyType: type,
				submit: postCreateCompany,
			});
		}
		if (role === 'member') {
			if (selectedCompanyObj) {
				setDialogData({
					companyName: selectedCompanyObj.company_name,
					ceoName: selectedCompanyObj.member_name,
					companyType: selectedCompanyObj.company_type,
					submit: (grade?: string) => postJoinCompany(grade || '구매담당자'),
				});
			}
		}
		setOpen(true);
	};

	const postCreateCompany = async () => {
		if (disabled) {
			enqueueSnackbar(ToastStr.COMPANY_CHECK_ALL_INPUT_STR, { variant: 'warning' });
			return;
		}
		try {
			axios({
				method: 'POST',
				data: {
					classCode: member.classCode,
					companyName: companyName,
					name: member.name,
					email: member.id,
					type: type,
				},
				withCredentials: true,
				url: SERVER + v_member.member + v_member.company_foundation,
			})
				.then((res) => res.data)
				.then(async (data: any) => {
					if (data.code === Values.SUCCESS_CODE) {
						await setMember(Object.assign({}, member, { companyNum: data.companies.companyNum, companyType: type }));
						history.push({
							pathname: '/CompanyInfo',
						});
						enqueueSnackbar(ToastStr.COMPANY_FOUNDATION_SUCCESS, {
							variant: 'success',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else if (data.code === Values.COMPANY_EXIST_CODE) {
						enqueueSnackbar(ToastStr.COMPANY_EXIST_STR, { variant: 'warning' });
					}
				});
		} catch (e: any) {
			enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, { variant: 'error' });
		}
	};

	const postJoinCompany = async (grade: string) => {
		if (disabled) {
			enqueueSnackbar(t('companyProcess.needInputs'), { variant: 'warning' });
			return;
		}
		axios({
			method: 'POST',
			data: {
				companyNum: selectedCompany, // company_num selected
				email: member.id,
				grade: grade,
			},
			withCredentials: true,
			url: SERVER + v_member.member + v_member.company_app,
		})
			.then((res) => {
				return res.data;
			})
			.then((data: any) => {
				console.log(data);
				if (data.code == Values.SUCCESS_CODE) {
					enqueueSnackbar(ToastStr.COMPANY_APP_SUCCESS_STR, {
						variant: 'success',
						autoHideDuration: SNACKBAR_TIME,
					});
					setMember(Object.assign({}, member, { companyNum: -1 }));
					history.push({
						pathname: '/CompanyAppStandBy',
					});
				} else {
					if ((data.code === Values.FAIL_CODE && data.msg.code === 'ER_DUP_ENTRY') || data.code === Values.COMPANY_ALREADY_APPLIED_CODE) {
						setMember(Object.assign({}, member, { companyNum: -1 }));
						history.push({ pathname: '/CompanyAppStandBy' });
						enqueueSnackbar(ToastStr.COMPANY_APP_DUPLICATE_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else if (data.code == Values.FAIL_CODE) {
						enqueueSnackbar(ToastStr.COMPANY_APP_FAIL_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else {
						enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
							variant: 'error',
							autoHideDuration: SNACKBAR_TIME,
						});
					}
				}
			});
	};

	const loadCompanyList = async () => {
		setLoadingList(true);
		try {
			const { data } = await axios.get(SERVER + v_member.member + v_member.company_list, {
				params: { classCode: member.classCode },
				withCredentials: true,
			});
			if (data && data.code === Values.SUCCESS_CODE && Array.isArray(data.companies)) {
				setCompanyList(data.companies);
			} else {
				setCompanyList([]);
			}
		} catch (e) {
			setCompanyList([]);
		} finally {
			setLoadingList(false);
		}
	};

	useEffect(() => {
		if (role === 'member') {
			loadCompanyList();
		}
	}, [role, member?.classCode]);

	return (
		<div className={classes.container} data-testid="company-process-dashboard">
			<div className={classes.header}>
				<IconButton className={classes.back} onClick={goBack}>
					<img src="/images/back.png" style={{ width: 16, height: 16 }} />
				</IconButton>
				<div className={classes.image}>
					<img src="/images/playstartup_color.webp" alt="PLAY STARTUP" />
				</div>
			</div>

			<div className={classes.content}>
				{/* 역할 */}
				<div className={classes.section}>
					<Typography className={classes.sectionTitle}>{t('companyProcess.role')}</Typography>
					<div className={classes.roleToggleWrap}>
						<Button className={clsx(classes.roleBtn, { active: role === 'leader' })} onClick={() => setRole('leader')} variant="outlined" data-testid="role-leader-btn">
							{t('companyProcess.leader')}
						</Button>
						<Button className={clsx(classes.roleBtn, { active: role === 'member' })} onClick={() => setRole('member')} variant="outlined" data-testid="role-member-btn">
							{t('companyProcess.member')}
						</Button>
					</div>
				</div>
				{role === 'leader' && (
					<div>
						{/* 기업 유형 */}
						<div className={classes.section}>
							<Typography className={classes.sectionTitle}>{t('companyProcess.companyType')}</Typography>
							<div className={classes.cardGrid}>
								<Paper elevation={0} className={clsx(classes.typeCard, { active: type === 'M' })} onClick={() => setType('M')} data-testid="company-type-manu">
									{type === 'M' ? <img src="/images/manufacture_color.png" alt="제조 기업" /> : <img src="/images/manufacture.png" alt="제조 기업" />}
									<Typography className="label">{t('companyProcess.typeM')}</Typography>
								</Paper>
								<Paper elevation={0} className={clsx(classes.typeCard, { active: type === 'D' })} onClick={() => setType('D')} data-testid="company-type-dist">
									{type === 'D' ? <img src="/images/distribution_color.png" alt="유통 기업" /> : <img src="/images/distribution.png" alt="유통 기업" />}
									<Typography className="label">{t('companyProcess.typeD')}</Typography>
								</Paper>
							</div>
						</div>

						{/* 기업 이름 */}
						<div className={classes.section}>
							<Typography className={classes.sectionTitle}>{t('companyProcess.companyName')}</Typography>
							<TextField
								value={companyName}
								fullWidth
								onChange={(e) => setCompanyName(e.target.value)}
								placeholder={t('companyProcess.companyNamePlaceholder')}
								variant="standard"
								className={classes.nameField}
								autoComplete="off"
								inputProps={{ maxLength: 15, 'data-testid': 'company-name-input' }}
							/>
						</div>
					</div>
				)}
				{role === 'member' && (
					<div className={classes.section}>
						<div className={classes.companyHeader}>
							<Typography className={classes.sectionTitle}>{t('companyProcess.companyList')}</Typography>
							<IconButton className={classes.refreshBtn} onClick={loadCompanyList} disabled={loadingList}>
								<img src="/images/refresh.webp" alt="refresh" style={{ width: 24, height: 24 }} />
							</IconButton>
						</div>
						<Divider className={classes.listDivider} />
						<div className={classes.companyListWrap}>
							{companyList.map((c: any) => {
								const name = c.company_name;
								const ceo = c.member_name;
								const active = selectedCompany === c.company_num;
								return (
									<div
										key={c.company_num}
										className={clsx(classes.companyItem, { [classes.companyItemActive]: active })}
										onClick={() => setSelectedCompany(c.company_num)}
										data-testid={`company-name-${c.company_name}`}
									>
										<div className={classes.companyLeft}>
											<div className={clsx(classes.radioOuter, { [classes.radioOuterActive]: active })}>
												<div className={clsx(classes.radioInner, { [classes.radioInnerActive]: active })} />
											</div>
											<Typography className={clsx(classes.companyName, { [classes.companyNameActive]: active })}>{name}</Typography>
										</div>
										<Typography className={clsx(classes.companyCeo, { [classes.companyCeoActive]: active })}>
											{t('companyProcess.ceoPrefix')}: {typeof ceo === 'string' ? ceo.replace(/^대표[:\s]*/, '') : ceo}
										</Typography>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>

			<div className={classes.footer}>
				<Button className={classes.submit} variant="contained" disabled={disabled} onClick={openDialog} data-testid="company-submit-btn">
					{role === 'leader' ? t('companyProcess.createCompany') : t('companyProcess.applyCompany')}
				</Button>
			</div>
			<DialogCompanyProcess
				open={open}
				onClose={() => setOpen(false)}
				companyName={dialogData?.companyName}
				ceoName={dialogData?.ceoName}
				companyType={dialogData?.companyType}
				defaultRole={role}
				onSubmit={dialogData?.submit}
			/>
		</div>
	);
};

export default CompanyProcess;
